import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Search, Filter, Download, DollarSign, TrendingUp, AlertCircle, Calendar } from 'lucide-react';
import { apiRequest } from '../../config/api';
import { toast } from '../ui/use-toast';

const AdminRevenueTab = ({ patients = [], doctors = [] }) => {
  const [revenueData, setRevenueData] = useState([]);
  const [summary, setSummary] = useState({
    total_revenue: 0,
    total_paid: 0,
    total_pending: 0
  });
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [selectedPatient, setSelectedPatient] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [patientNameSearch, setPatientNameSearch] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState('');
  const [expandedMonths, setExpandedMonths] = useState(new Set());

  // Generate years list (current year and previous 5 years)
  const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);
  
  // Months list
  const months = [
    { value: '', label: 'All Months' },
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  useEffect(() => {
    fetchRevenueData();
  }, [selectedPatient, selectedDoctor, patientNameSearch, selectedYear, selectedMonth]);

  const fetchRevenueData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (selectedPatient) {
        params.append('patient_id', selectedPatient);
      }
      
      if (selectedDoctor) {
        params.append('doctor_id', selectedDoctor);
      }
      
      if (patientNameSearch.trim()) {
        params.append('patient_name', patientNameSearch.trim());
      }
      
      params.append('year', selectedYear);
      
      if (selectedMonth) {
        params.append('month', selectedMonth);
      }

      const data = await apiRequest(`get_revenue_report.php?${params.toString()}`);
      
      if (data.success) {
        setRevenueData(data.revenue || []);
        setSummary(data.summary || {
          total_revenue: 0,
          total_paid: 0,
          total_pending: 0
        });
      } else {
        toast({
          title: 'Error',
          description: data.error || 'Failed to fetch revenue data',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error fetching revenue data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load revenue data. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleMonthExpansion = (monthKey) => {
    setExpandedMonths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(monthKey)) {
        newSet.delete(monthKey);
      } else {
        newSet.add(monthKey);
      }
      return newSet;
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const clearFilters = () => {
    setSelectedPatient('');
    setSelectedDoctor('');
    setPatientNameSearch('');
    setSelectedYear(new Date().getFullYear());
    setSelectedMonth('');
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="glass-effect border-gray-700">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-xs sm:text-sm mb-1">Total Revenue</p>
                <p className="text-xl sm:text-2xl font-bold text-white">
                  {formatCurrency(summary.total_revenue)}
                </p>
              </div>
              <DollarSign className="h-8 w-8 sm:h-10 sm:w-10 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="glass-effect border-gray-700">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-xs sm:text-sm mb-1">Total Paid</p>
                <p className="text-xl sm:text-2xl font-bold text-white">
                  {formatCurrency(summary.total_paid)}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 sm:h-10 sm:w-10 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="glass-effect border-gray-700">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-xs sm:text-sm mb-1">Pending</p>
                <p className="text-xl sm:text-2xl font-bold text-white">
                  {formatCurrency(summary.total_pending)}
                </p>
              </div>
              <AlertCircle className="h-8 w-8 sm:h-10 sm:w-10 text-yellow-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="glass-effect border-gray-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label htmlFor="year">Year</Label>
              <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(parseInt(value))}>
                <SelectTrigger id="year" className="bg-gray-800 border-gray-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  {years.map(year => (
                    <SelectItem key={year} value={year.toString()} className="text-white hover:bg-gray-700">
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="month">Month</Label>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger id="month" className="bg-gray-800 border-gray-600 text-white">
                  <SelectValue placeholder="All Months" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  {months.map(month => (
                    <SelectItem key={month.value} value={month.value} className="text-white hover:bg-gray-700">
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="patient">Patient</Label>
              <Select value={selectedPatient} onValueChange={setSelectedPatient}>
                <SelectTrigger id="patient" className="bg-gray-800 border-gray-600 text-white">
                  <SelectValue placeholder="All Patients" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  <SelectItem value="" className="text-white hover:bg-gray-700">All Patients</SelectItem>
                  {patients.map(patient => (
                    <SelectItem key={patient.id} value={patient.id.toString()} className="text-white hover:bg-gray-700">
                      {patient.full_name || patient.name || `Patient ${patient.id}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="doctor">Doctor</Label>
              <Select value={selectedDoctor} onValueChange={setSelectedDoctor}>
                <SelectTrigger id="doctor" className="bg-gray-800 border-gray-600 text-white">
                  <SelectValue placeholder="All Doctors" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  <SelectItem value="" className="text-white hover:bg-gray-700">All Doctors</SelectItem>
                  {doctors.map(doctor => (
                    <SelectItem key={doctor.id} value={doctor.id.toString()} className="text-white hover:bg-gray-700">
                      {doctor.full_name || doctor.username || `Doctor ${doctor.id}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="patientNameSearch">Search Patient Name</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="patientNameSearch"
                  placeholder="Search by name..."
                  value={patientNameSearch}
                  onChange={(e) => setPatientNameSearch(e.target.value)}
                  className="pl-10 bg-gray-800 border-gray-600 text-white"
                />
              </div>
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <Button
              onClick={clearFilters}
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Revenue Data */}
      <Card className="glass-effect border-gray-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Monthly Revenue Report
          </CardTitle>
          <CardDescription>
            Revenue breakdown by month with patient and doctor details
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-400">Loading revenue data...</div>
          ) : revenueData.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              No revenue data found for the selected filters.
            </div>
          ) : (
            <div className="space-y-4">
              {revenueData.map((monthData) => (
                <motion.div
                  key={monthData.month_key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="border border-gray-700 rounded-lg overflow-hidden"
                >
                  {/* Month Header */}
                  <div
                    className="bg-gray-800/50 p-4 cursor-pointer hover:bg-gray-800 transition-colors"
                    onClick={() => toggleMonthExpansion(monthData.month_key)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold text-white">
                          {monthData.month_label}
                        </h3>
                        <span className="text-xs text-gray-400">
                          ({monthData.records.length} {monthData.records.length === 1 ? 'record' : 'records'})
                        </span>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-xs text-gray-400">Total Revenue</p>
                          <p className="text-sm font-semibold text-green-400">
                            {formatCurrency(monthData.total_revenue)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-400">Paid</p>
                          <p className="text-sm font-semibold text-blue-400">
                            {formatCurrency(monthData.total_paid)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-400">Pending</p>
                          <p className="text-sm font-semibold text-yellow-400">
                            {formatCurrency(monthData.total_pending)}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-gray-400 hover:text-white"
                        >
                          {expandedMonths.has(monthData.month_key) ? '▼' : '▶'}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Month Details */}
                  {expandedMonths.has(monthData.month_key) && (
                    <div className="p-4 bg-gray-900/50">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-700">
                              <th className="text-left p-2 text-gray-400 font-medium">Patient</th>
                              <th className="text-left p-2 text-gray-400 font-medium">Doctor</th>
                              <th className="text-right p-2 text-gray-400 font-medium">Total</th>
                              <th className="text-right p-2 text-gray-400 font-medium">Paid</th>
                              <th className="text-right p-2 text-gray-400 font-medium">Pending</th>
                              <th className="text-left p-2 text-gray-400 font-medium">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {monthData.records.map((record) => (
                              <tr
                                key={record.id}
                                className="border-b border-gray-800 hover:bg-gray-800/30 transition-colors"
                              >
                                <td className="p-2 text-white">{record.patient_name}</td>
                                <td className="p-2 text-gray-300">{record.doctor_name}</td>
                                <td className="p-2 text-right text-white font-medium">
                                  {formatCurrency(record.total)}
                                </td>
                                <td className="p-2 text-right text-green-400">
                                  {formatCurrency(record.paid)}
                                </td>
                                <td className="p-2 text-right text-yellow-400">
                                  {formatCurrency(record.due)}
                                </td>
                                <td className="p-2 text-gray-400">
                                  {new Date(record.created_at).toLocaleDateString('en-IN', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric'
                                  })}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminRevenueTab;
